import { create } from 'zustand'
import { useProfileStore as useCoreProfileStore } from '@flows.cash/core'
import { toast } from 'sonner'
import i18next from 'i18next'
import { profileKeys } from '@flows.cash/core'
import { queryClient } from '@/lib/queryClient'

// Create a wrapper store that adds toast notifications to the core profile store
export const useProfileStore = create((set) => {
  console.log('Initializing web profile store');
  const coreStore = useCoreProfileStore.getState()
  
  // Log the initial state
  console.log('Web profile store - initial state:', {
    hasSettings: !!coreStore.settings,
    isLoading: coreStore.isLoading
  });

  // Subscribe to changes in the core store
  useCoreProfileStore.subscribe(
    (state) => {
      console.log('Core profile store updated, syncing web store');
      set({
        settings: state.settings,
        isLoading: state.isLoading,
        error: state.error
      });
    }
  );
  
  return {
    ...coreStore,
    
    // Override fetchSettings to use TanStack Query
    fetchSettings: async (userId: string) => {
      console.log('Web profile store - fetchSettings called for user:', userId);
      
      // Check if we already have the data in the cache
      const cachedData = queryClient.getQueryData(profileKeys.settings(userId));
      
      if (cachedData) {
        console.log('Web profile store - using cached settings data');
        return;
      }
      
      try {
        // Prefetch the data using the query client
        await queryClient.prefetchQuery({
          queryKey: profileKeys.settings(userId),
          queryFn: async () => {
            // Call the core store's fetchSettings
            await coreStore.fetchSettings(userId);
            
            // Return the settings from the core store
            return useCoreProfileStore.getState().settings;
          },
          staleTime: 1000 * 60 * 5, // 5 minutes
        });
        
        console.log('Web profile store - fetchSettings successful');
        
        // Force update from core store
        const updatedCoreStore = useCoreProfileStore.getState();
        set({
          settings: updatedCoreStore.settings,
          isLoading: updatedCoreStore.isLoading,
          error: updatedCoreStore.error
        });
      } catch (error) {
        console.error('Web profile store - fetchSettings failed:', error);
        // Don't throw the error, just log it and show a toast
        toast.error(i18next.t('profile.settings.fetchError'));
      }
    },
    
    updateSettings: async (userId: string, data: Parameters<typeof coreStore.updateSettings>[1]) => {
      console.log('Web profile store - updateSettings called');
      try {
        await coreStore.updateSettings(userId, data)
        console.log('Web profile store - updateSettings successful');
        
        // Force update from core store
        const updatedCoreStore = useCoreProfileStore.getState();
        set({
          settings: updatedCoreStore.settings,
          isLoading: updatedCoreStore.isLoading,
          error: updatedCoreStore.error
        });
        
        // Invalidate the query to refetch the latest data
        queryClient.invalidateQueries({ queryKey: profileKeys.settings(userId) });
        
        toast.success(i18next.t('profile.settings.updateSuccess'))
      } catch (error) {
        console.error('Web profile store - updateSettings failed:', error);
        toast.error(i18next.t('profile.settings.updateError'))
        throw error
      }
    },

    updateProfilePhoto: async (userId: string, photoUrl: string | null) => {
      console.log('Web profile store - updateProfilePhoto called');
      try {
        await coreStore.updateProfilePhoto(userId, photoUrl)
        console.log('Web profile store - updateProfilePhoto successful');
        
        // Force update from core store
        const updatedCoreStore = useCoreProfileStore.getState();
        set({
          settings: updatedCoreStore.settings,
          isLoading: updatedCoreStore.isLoading,
          error: updatedCoreStore.error
        });
        
        // Invalidate the query to refetch the latest data
        queryClient.invalidateQueries({ queryKey: profileKeys.settings(userId) });
        
        toast.success(i18next.t('profile.photo.updateSuccess'))
      } catch (error) {
        console.error('Web profile store - updateProfilePhoto failed:', error);
        toast.error(i18next.t('profile.photo.updateError'))
        throw error
      }
    }
  }
})